import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import PaymentTestModeBanner from "@/components/PaymentTestModeBanner";
import LazyChunkBoundary from "@/components/LazyChunkBoundary";
// Toaster + Sonner are tiny and live at the root on every page. Lazy-loading
// them through React.lazy strips Sonner's forwardRef metadata and floods the
// dev console with "Function components cannot be given refs" on every nav.
// Eager import is correct here.
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
// AgeGate / CookieBanner / OnboardingFlow are non-LCP overlays. They are
// imported via direct dynamic import on idle (not React.lazy + Suspense) to
// keep framer-motion and other overlay code off the initial JS critical path
// without triggering the forwardRef warnings seen with lazy + Suspense.
import { useLiveBuyPulse } from "@/hooks/useLiveBuyPulse";
import SessionLimitBanner from "@/components/SessionLimitBanner";
import { captureInboundRef } from "@/lib/share/referral";

// Capture inbound ?ref=<code> from any landing URL. Runs once at module
// load — first-touch attribution. Safe to call before React mounts.
if (typeof window !== "undefined") {
  captureInboundRef();
}

function LiveBuyPulseRunner() {
  useLiveBuyPulse();
  return null;
}

function LiveBuyPulseMount() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Skip the realtime subscription for headless crawlers (Lighthouse,
    // PageSpeed Insights, Googlebot rendering). Their environments often
    // can't open the realtime WebSocket, which logs ERR_NAME_NOT_RESOLVED
    // console errors that hurt the Best Practices audit. Real users have
    // navigator.webdriver === false.
    if (typeof navigator !== "undefined" && (navigator as any).webdriver) return;
    const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
    const handle = idle(() => setReady(true));
    return () => {
      const cancel = (window as any).cancelIdleCallback;
      if (cancel && typeof handle === "number") cancel(handle);
    };
  }, []);
  if (!ready) return null;
  return <LiveBuyPulseRunner />;
}

// Defer-mount any component (default export) until browser idle. Avoids
// React.lazy/Suspense forwardRef interaction issues for overlay components.
function IdleMount({ load }: { load: () => Promise<{ default: React.ComponentType }> }) {
  const [Comp, setComp] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    let cancelled = false;
    const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
    const handle = idle(() => {
      load().then((m) => {
        if (!cancelled) setComp(() => m.default);
      });
    });
    return () => {
      cancelled = true;
      const cancel = (window as any).cancelIdleCallback;
      if (cancel && typeof handle === "number") cancel(handle);
    };
  }, [load]);
  if (!Comp) return null;
  return <Comp />;
}

import Index from "./pages/Index";
const Feed = lazy(() => import("./pages/Feed"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Alerts = lazy(() => import("./pages/Alerts"));
const History = lazy(() => import("./pages/History"));
const Profile = lazy(() => import("./pages/Profile"));
const Auth = lazy(() => import("./pages/Auth"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const CheckoutTest = lazy(() => import("./pages/CheckoutTest"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SharedDecision = lazy(() => import("./pages/SharedDecision"));
const TrackRecord = lazy(() => import("./pages/TrackRecord"));
const Mistakes = lazy(() => import("./pages/Mistakes"));
const Methodology = lazy(() => import("./pages/Methodology"));
const About = lazy(() => import("./pages/About"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const EdgeDrop = lazy(() => import("./pages/EdgeDrop"));
const Receipt = lazy(() => import("./pages/Receipt"));
const BrandPreview = lazy(() => import("./pages/BrandPreview"));
const PredictionMarket = lazy(() => import("./pages/PredictionMarket"));
const LegacyPredictionRedirect = lazy(() => import("./pages/LegacyPredictionRedirect"));
const Playbook = lazy(() => import("./pages/Playbook"));
const AdminCost = lazy(() => import("./pages/AdminCost"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PaymentTestModeBanner />
          <SessionLimitBanner />
          <LiveBuyPulseMount />
          <Suspense fallback={<div className="min-h-screen bg-background" aria-hidden />}>
            <LazyChunkBoundary>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/watchlist" element={<Watchlist />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/history" element={<History />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/checkout/test" element={<CheckoutTest />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refunds" element={<Refunds />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/v/:decisionId" element={<SharedDecision />} />
                <Route path="/track-record" element={<TrackRecord />} />
                <Route path="/mistakes" element={<Mistakes />} />
                <Route path="/methodology" element={<Methodology />} />
                <Route path="/about" element={<About />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/edge-drop" element={<EdgeDrop />} />
                <Route path="/receipt/:id" element={<Receipt />} />
                <Route path="/brand/preview" element={<BrandPreview />} />
                <Route path="/m/:slug" element={<PredictionMarket />} />
                <Route path="/prediction/:slug" element={<LegacyPredictionRedirect />} />
                <Route path="/playbook" element={<Playbook />} />
                <Route path="/admin/cost" element={<AdminCost />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LazyChunkBoundary>
          </Suspense>
          <BottomNav />
          <IdleMount load={() => import("@/components/OnboardingFlow")} />
          <IdleMount load={() => import("@/components/AgeGate")} />
          <IdleMount load={() => import("@/components/CookieBanner")} />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
